import { CountryStates } from "@/constants/states";
import { EMAIL_REGEXP } from "@/constants/regexp";
import type { Recipient } from "@/types/recipients";

export function useValidation() {
  const requiredValidator = (v: any): boolean | string =>
    (v !== "" && v !== null && v !== undefined && v.toString().trim() !== "") || "Field is required";

  const emailValidator = (email: string): boolean | string => EMAIL_REGEXP.test(email) || "Email is not valid";

  const digitsValidator = (v: any): boolean | string =>
    (v.toString() && !isNaN(v) && !isNaN(parseFloat(v))) || "Only digits allowed";

  const selectValidator = (v: any[]): boolean | string => (v && v.length > 0) || "Required at least one value";

  const phoneValidator = (v: string): boolean | string => {
    const len = (v || "").replace(/[^.\d]/g, "").length;

    if (len === 0) {
      return true;
    }

    return len === 11 || "Phone number is incorrect";
  };

  const validateVIN = (v: string): boolean | string => (v && v.length === 17) || "VIN code is incorrect";

  const stateValidator = (v: string): boolean | string => {
    if (v && v.length !== 2) return "Two letters state name";

    const code = v.toUpperCase();

    const allStates = Object.values(CountryStates).flat();

    return (code && allStates.includes(code)) || "State not found";
  };

  const dateInputValidator = (v: string): boolean | string => {
    const re = /^[0-1]\d\/[0-3]\d\/\d{4}$/;
    return re.test(v) || "Date is not valid";
  };

  const yearLengthValidator = (v: string): boolean | string => !v || (v && String(v).length === 4) || "Incorrect year";

  const positiveNumber = (v: number): boolean | string =>
    (v && !!Number(v) && v > 0) || "Only positive numbers allowed";

  const fileRequiredValidator = (v: File[]): boolean | string => (v[0] && !!v[0].name) || "File is required";

  const zipCodeValidator = (v: string): boolean | string => {
    return (v?.length >= 5 && v?.length <= 7) || "Zip code must be between 5 and 7 characters";
  };

  const licensePlateValidator = (v: string): boolean | string => {
    return (v && v.length === 10) || "License plate is incorrect";
  };

  const licenseStateValidator = (v: string): boolean | string => {
    return (v && v.length === 2) || "License state is incorrect";
  };

  const yearFromValidator = (v: string): boolean | string => {
    return (v && v.length === 4) || "Year from is incorrect";
  };

  const arrayRequiredValidator = (v: any[]): boolean | string => {
    return (Array.isArray(v) && v.length > 0) || "Field is required";
  };

  const notFoundedRecipientsValidator = (v: Recipient[]): boolean | string => {
    const notFoundedEmails = v.filter((recipient) => recipient.notFounded).map((item) => item.value);
    const errorMessage = `Emails: ${notFoundedEmails.join(", ")} not found`;

    return !notFoundedEmails.length || errorMessage;
  };

  const phoneExtValidator = (v: string): boolean | string => {
    if (!v) {
      return true;
    }

    return v.length === 3 || "Phone ext is incorrect";
  };

  const timeFormatValidator = (v: string): boolean | string => {
    if (!v) return true;
    return /^[0-2][0-9]:[0-5][0-9]$/.test(v) || "Wrong time format";
  };

  return {
    requiredValidator,
    emailValidator,
    digitsValidator,
    selectValidator,
    phoneValidator,
    validateVIN,
    stateValidator,
    dateInputValidator,
    yearLengthValidator,
    positiveNumber,
    fileRequiredValidator,
    zipCodeValidator,
    licensePlateValidator,
    yearFromValidator,
    licenseStateValidator,
    arrayRequiredValidator,
    notFoundedRecipientsValidator,
    phoneExtValidator,
    timeFormatValidator,
  };
}
